export const CAREER_SOURCE = 'website-careers-page';

export const APPLICATION_STATUSES = [
  'New',
  'Reviewed',
  'Shortlisted',
  'Rejected',
] as const;

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export type ApplicationTimelineEntry = {
  type?: string;
  status?: string | null;
  previousStatus?: string | null;
  nextStatus?: string | null;
  note?: string | null;
  role?: string | null;
  byEmail?: string | null;
  at?: unknown;
};

export function isCareerApplicationSource(value: unknown): boolean {
  return String(value || '').trim().toLowerCase() === CAREER_SOURCE;
}

export function normalizeApplicationStatus(
  value: unknown
): ApplicationStatus {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'reviewed') return 'Reviewed';
  if (raw === 'shortlisted') return 'Shortlisted';
  if (raw === 'rejected') return 'Rejected';
  return 'New';
}

export function normalizeApplicantEmail(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

export function normalizeApplicantPhone(value: unknown): string {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length > 10) return digits.slice(-10);
  return digits;
}

export function buildApplicantKey(phone: unknown, email: unknown): string | null {
  const normalizedPhone = normalizeApplicantPhone(phone);
  if (normalizedPhone) return `phone:${normalizedPhone}`;
  const normalizedEmail = normalizeApplicantEmail(email);
  if (normalizedEmail) return `email:${normalizedEmail}`;
  return null;
}

export function formatTimelineLabel(entry: ApplicationTimelineEntry): string {
  const type = String(entry?.type || '').trim();
  if (type === 'application-created') {
    const role = String(entry?.role || '').trim();
    return role ? `Applied for ${role}` : 'Application received';
  }
  if (type === 'status-updated') {
    const nextStatus = normalizeApplicationStatus(entry?.nextStatus || entry?.status);
    const previousStatus = String(entry?.previousStatus || '').trim();
    return previousStatus ?
      `Status changed: ${previousStatus} -> ${nextStatus}` :
      `Status changed to ${nextStatus}`;
  }
  if (type === 'note-added') return 'Decision note updated';
  return 'Application updated';
}
